
import { computed, defineComponent, nextTick, reactive, ref } from 'vue';
import cloneDeep from 'lodash/cloneDeep';

import { IClaim, IClaimKey, IClaimMedia, IClaimNoteOrReply, IClaimTypeSelections } from '@/types';
import { CLAIM_STATUS, CLAIM_STATUS_LABEL_MAP, CLAIM_TYPE, CLAIM_TYPE_LABEL_MAP } from '@/data';
import { useClaims, useTaxonomy } from '@/hooks';
import ClaimMediaGallery from '@/components/ClaimMediaGallery.vue';
import ClaimNotes from '@/components/ClaimNotes.vue';
import InlineEditField from '@/components/InlineEditField.vue';
import ClaimTypeSelector from '@/components/ClaimTypeSelector.vue';
import { formatDisplayDateTime } from '@/utils';
import ReplacementItemList from '@/components/ReplacementItemList.vue';

export default defineComponent({
  components: {
    ClaimMediaGallery,
    ClaimNotes,
    InlineEditField,
    ClaimTypeSelector,
    ReplacementItemList,
  },
  name: 'ClaimListItem',
  props: {
    claim: Object,
  },
  emits: ['update', 'save-note'],
  setup(props, { emit }) {
    const {
      publicResolutions,
      claimTypes,
      getSupplierName,
      getResolutionName,
      getSupportRequestIssueTypeName,
    } = useTaxonomy();
    const { getProductBoxLotNo } = useClaims();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const claimLocal = reactive<IClaim | Record<string, any>>({
      id: null,
      ...cloneDeep(props.claim),
    }); // copy the claim data to local for write purpose
    const canClaim = computed<boolean>(() =>
      Boolean(
        claimLocal.categoryId && claimLocal.typeId && claimLocal.issueId && claimLocal.subIssueId
      )
    ); // flag, only applies to new tickets
    const dropdownOptions = reactive({
      resolutionTypeOptions: publicResolutions,
    });
    // computed statuses match with left sidebar
    const isSupportRequest = computed<boolean>(
      () => claimLocal.status === CLAIM_STATUS.CUSTOMER_CARE_OPEN
    );
    const isNewTicket = computed<boolean>(
      () => claimLocal.status === CLAIM_STATUS.CUSTOMER_CARE_RESOLVED
    );
    const isOpenClaim = computed<boolean>(
      () =>
        claimLocal.status === CLAIM_STATUS.QA_OPEN || claimLocal.status === CLAIM_STATUS.QA_COMPLETE
    );
    const isPendingClaim = computed<boolean>(
      () =>
        claimLocal.status === CLAIM_STATUS.QA_SUBMITTED ||
        claimLocal.status === CLAIM_STATUS.QA_PENDING ||
        claimLocal.status === CLAIM_STATUS.QA_SETTLED
    );
    const isArchived = computed<boolean>(() => claimLocal.status === CLAIM_STATUS.QA_CLOSED);

    const isValidProposalAndStatus = computed<boolean>(
      () => claimLocal.proposedAmount !== null && claimLocal.status === CLAIM_STATUS.QA_PENDING
    );
    // input refs
    const descriptionInput = ref<HTMLElement | null>(null);
    const requestedAmountInput = ref<HTMLElement | null>(null);
    const finalAmountInput = ref<HTMLElement | null>(null);

    // build the payload only contains the necessary fields that will pass to the backend
    const buildClaimPayload = (fields: IClaimKey[]): IClaim => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = { id: claimLocal.id };
      fields.forEach((field: IClaimKey) => {
        payload[field] = claimLocal[field as keyof IClaim];
      });

      return payload;
    };

    // handle status update
    const updateClaimStatus = (status: string) => {
      claimLocal.status = status;
    };

    // handle claim type update
    const updateClaimType = (type: string | null) => {
      claimLocal.claimType = type;
    };

    // update claim by fields
    const updateClaim = (fields: IClaimKey[]) => {
      if (!props.claim?.id) return;

      const payload = buildClaimPayload(fields);

      emit('update', payload);
    };
    const onAcceptVendorProposal = () => {
      claimLocal.finalAmount = claimLocal.proposedAmount;
      updateClaimStatus(CLAIM_STATUS.QA_SETTLED);
      updateClaim(['finalAmount', 'status']);
    };
    const onSaveEdit = (fieldName: IClaimKey) => {
      // return if the input field value is the same as the original
      if (
        props.claim &&
        props.claim[fieldName] &&
        props.claim[fieldName].toString() === claimLocal[fieldName]?.toString()
      )
        return;

      if (fieldName === 'requestedAmount') {
        claimLocal.requestedAmount = claimLocal.requestedAmount
          ? Number(claimLocal.requestedAmount)
          : null;

        // only update status if in is Open Claim
        if (claimLocal.requestedAmount) {
          updateClaimType(CLAIM_TYPE.CREDIT_REQUESTED);
          if (isOpenClaim.value) {
            updateClaimStatus(CLAIM_STATUS.QA_COMPLETE);
          }
        } else {
          updateClaimType(null);
          if (isOpenClaim.value) {
            updateClaimStatus(CLAIM_STATUS.QA_OPEN);
          }
        }

        updateClaim(['claimType', 'requestedAmount', 'status']);
      } else if (fieldName === 'finalAmount') {
        updateClaimStatus(CLAIM_STATUS.QA_SETTLED);
        updateClaim(['finalAmount', 'status']);
      } else {
        updateClaim([fieldName]);
      }
    };

    const onCancelEdit = (fieldName: IClaimKey) => {
      if (!props.claim) return;

      claimLocal[fieldName as keyof IClaim] = props.claim[fieldName];
    };

    const onFocusInput = async (inputRef: string) => {
      await nextTick();

      switch (inputRef) {
        case 'descriptionInput':
          descriptionInput.value?.focus();
          break;
        case 'requestedAmountInput':
          requestedAmountInput.value?.focus();
          break;
        case 'finalAmountInput':
          finalAmountInput.value?.focus();
          break;
        default:
          break;
      }
    };

    const onClaim = (shouldClaim: boolean) => {
      if (shouldClaim) {
        updateClaimStatus(CLAIM_STATUS.QA_OPEN);
      } else {
        updateClaimStatus(CLAIM_STATUS.QA_CLOSED);
      }

      updateClaim(['status', 'categoryId', 'typeId', 'issueId', 'subIssueId']);
    };

    function onReopen() {
      updateClaimStatus(CLAIM_STATUS.QA_OPEN);

      updateClaim(['status', 'categoryId', 'typeId', 'issueId', 'subIssueId']);
    }

    const selectClaimType = (claimType: string) => {
      if (claimType === claimLocal.claimType) {
        updateClaimType(null);
      } else {
        updateClaimType(claimType);
      }

      // if select Request Acknowledgement and is open claim, auto convert to complete
      if (claimType === CLAIM_TYPE.REQUEST_ACKNOWLEDGMENT && isOpenClaim.value) {
        updateClaimStatus(CLAIM_STATUS.QA_COMPLETE);
        updateClaim(['claimType', 'status']);
      }
    };

    const toggleShareMedia = (media: IClaimMedia) => {
      if (!claimLocal.media) return;

      const currentMediaIndex = claimLocal.media.findIndex((m: IClaimMedia) => m.id === media.id);
      claimLocal.media[currentMediaIndex].isPrivate = media.isPrivate;

      updateClaim(['media']);
    };

    const onChangePublicResolutionId = (e: { target: HTMLInputElement }) => {
      const value = e.target.value;

      claimLocal.publicResolutionId = value === '' ? null : Number(value);

      if (claimLocal.publicResolutionId !== props.claim?.publicResolutionId) {
        updateClaim(['publicResolutionId']);
      }
    };

    function onSaveNote(value: IClaimNoteOrReply): void {
      emit('save-note', value, claimLocal.id);
    }

    const onClaimTypeSelected = (value: IClaimTypeSelections) => {
      claimLocal.categoryId = value.categoryId;
      claimLocal.typeId = value.typeId;
      claimLocal.issueId = value.issueId;
      claimLocal.subIssueId = value.subIssueId;
      if (
        (claimLocal.categoryId &&
          claimLocal.typeId &&
          claimLocal.issueId &&
          claimLocal.subIssueId) ||
        (claimLocal.categoryId === null &&
          claimLocal.typeId === null &&
          claimLocal.issueId === null &&
          claimLocal.subIssueId === null)
      ) {
        updateClaim(['categoryId', 'typeId', 'issueId', 'subIssueId']);
      }
    };

    const displayDateTime = (dateTime: number) => formatDisplayDateTime(dateTime, 'MMM Do, YYYY');

    // handle flagged update
    const onClaimFlagged = (flagged: boolean) => {
      claimLocal.flagged = flagged;
      updateClaim(['flagged']);
    };

    return {
      CLAIM_STATUS,
      CLAIM_STATUS_LABEL_MAP,
      CLAIM_TYPE,
      CLAIM_TYPE_LABEL_MAP,
      displayDateTime,
      claimLocal,
      dropdownOptions,
      onClaimTypeSelected,
      getSupplierName,
      getResolutionName,
      getProductBoxLotNo,
      getSupportRequestIssueTypeName,
      onSaveEdit,
      onCancelEdit,
      onClaim,
      onReopen,
      canClaim,
      claimTypes,
      selectClaimType,
      toggleShareMedia,
      descriptionInput,
      requestedAmountInput,
      finalAmountInput,
      onFocusInput,
      isSupportRequest,
      isNewTicket,
      isOpenClaim,
      isPendingClaim,
      isArchived,
      isValidProposalAndStatus,
      onAcceptVendorProposal,
      onChangePublicResolutionId,
      onSaveNote,
      onClaimFlagged,
    };
  },
});
